import type { Appointment, AppointmentState, Customer, Resource } from "@/lib/types.js";

/**
 * PRD calendar-ux-parity REQ-CUX-009/011/014: client-side filter pipeline for
 * the calendar grid. The filter is applied AFTER the data window is fetched
 * (PRD §"Search & filter"). For windows ≤ 1000 appointments this is well
 * under the 100ms budget on a Chromebook-class device.
 *
 * Default lifecycle filter (REQ-CUX-011) excludes `cancelled` and `no_show`.
 */

export const ALL_LIFECYCLE_STATES: readonly AppointmentState[] = [
  "pending",
  "confirmed",
  "pending_reschedule",
  "completed",
  "cancelled",
  "no_show"
];

export const DEFAULT_VISIBLE_STATES: readonly AppointmentState[] = [
  "pending",
  "confirmed",
  "pending_reschedule",
  "completed"
];

export interface CalendarFilters {
  customer?: string;
  services: string[];
  resources: string[];
  /** When empty list → use DEFAULT_VISIBLE_STATES. When non-empty → exact set. */
  states: string[];
  search: string;
}

/**
 * Apply filters in their documented order. The function takes precomputed
 * `customers` and `resources` maps for O(1) name lookup; callers usually
 * pass the same maps they already build for rendering, so this is free.
 * `resourcesById` is optional so existing call sites + tests that don't
 * care about the resource fallback path can keep their two-arg shape.
 */
export function applyFilters(
  appointments: Appointment[],
  filters: CalendarFilters,
  customersById: Map<string, Customer>,
  resourcesById?: Map<string, Resource>
): Appointment[] {
  const states = filters.states.length > 0 ? new Set(filters.states) : new Set(DEFAULT_VISIBLE_STATES);
  const services = filters.services.length > 0 ? new Set(filters.services) : null;
  const resources = filters.resources.length > 0 ? new Set(filters.resources) : null;
  const customer = filters.customer && filters.customer.length > 0 ? filters.customer : null;
  const searchLower = filters.search.trim().toLowerCase();

  return appointments.filter((appointment) => {
    if (!states.has(appointment.lifecycle_state)) return false;
    if (services && !services.has(appointment.service_id)) return false;
    if (resources && !resources.has(appointment.primary_resource_id)) return false;
    if (customer && appointment.customer_id !== customer) return false;
    if (searchLower.length > 0) {
      // PRD §"Search box" — substring against the appointment's denormalized
      // customer_name (preferred — populated by the CRM at booking time),
      // falling back to the legacy customers-table name for older rows, then
      // appointment.notes.
      //
      // PRD principal-scoped-reads-and-crm-refs (REQ-PSR-008): the search
      // also matches against the snapshotted `resource_name` so staff can
      // pull "all of Anya's appointments" with a single typed substring,
      // regardless of any later CRM-side rename. Legacy rows without a
      // snapshot fall back to the live resources-table name so the search
      // still hits them — same fallback strategy as customer_name.
      const displayName =
        appointment.customer_name ?? customersById.get(appointment.customer_id)?.name ?? "";
      const notes = appointment.notes ?? "";
      const resourceName =
        appointment.resource_name ??
        resourcesById?.get(appointment.primary_resource_id)?.name ??
        "";
      const haystack = `${displayName}\n${notes}\n${resourceName}`.toLowerCase();
      if (!haystack.includes(searchLower)) {
        return false;
      }
    }
    return true;
  });
}
